import { Injectable } from '@angular/core';
import { SSOService } from '@common/services/authentication/sso/sso.service';
import { ENPSService } from '@common/services/authentication/enps/enps.service';
import { HttpRequest, HttpClient } from '@angular/common/http';
import { IAuthService } from '@common/interfaces/i-auth.service'
import { Loader } from '@loader/loader';
import { AppService } from '@suite/app.service';
import { LogonService } from '@common/services/logon-service';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { Router } from '@angular/router';
import { ModalService } from '@common/components/modal/modal.service';
// import { JwtHelperService } from '@auth0/angular-jwt';
import jwtDecode, { JwtPayload } from "jwt-decode";
import { Util } from '@common/utils/util'

import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { NavigationService } from "@common/services/navigation-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { LayoutState } from "@common/services/layout-state";

declare var serverConfig: { [id: string]: any };
// const helper = new JwtHelperService({ tokenGetter: () => { } });

@Injectable({ providedIn: 'root' })
export class AuthService {
    cachedRequests: Array<HttpRequest<any>> = [];

    constructor(private ssoService: SSOService,
        private enpsService: ENPSService,
        private appService: AppService,
        private logonService: LogonService,
        private router: Router,
        private http: HttpClient,
        private timeoutQueue: TimeoutQueue,
        private modalService: ModalService,
        public nomWebServicesJson: NomWebServicesJson,
        public navigationService: NavigationService,
        public visualStateService: VisualStateService,
        public globalTablesService: GlobalTablesService,
        public layoutState: LayoutState
    ) { }

    public shouldReload: boolean = false;

    public getToken(): string {
        const token: string = '';
        if (localStorage.getItem("idToken")) return localStorage.getItem("idToken");
        return token;
    }

    public getAccessToken(): string {
        const token: string = '';
        if (localStorage.getItem("accessToken")) return localStorage.getItem("accessToken");
        return token;
    }

    public isTokenValid(tokenData: string): boolean {
        if (tokenData) {
            return !this.isTokenExpired(tokenData);
        }
        return false;
    }

    public getProviderName(): string {
        const provider: string = '';
        if (localStorage.getItem("providerName")) return localStorage.getItem("providerName");
        return provider;
    }

    public async loginWithToken(type: string, loader?: Loader): Promise<boolean> {
        const accessToken = this.getAccessToken();
        if (accessToken) {
            let res = await this.http.get('https://graph.facebook.com/v2.5/me?fields=id,email,first_name,last_name,link,name&access_token=' + accessToken, { responseType: 'json' }).toPromise();
            return;
        }
        const token = this.getToken();
        if (this.isTokenValid(token)) {
            const res = await this.login(type, loader);
            return res;
        } else {
            if (loader) loader.hide();
            this.nomWebServicesJson.clearSessionData();
            this.router.navigate(['/login']);
            return false;
        }
    }

    private async login(type: string, loader?: Loader): Promise<boolean> {
        if (loader) loader.show();
        let res = await this.ServiceFactory(type).loginWithToken();
        if (res.status) {
            this.initializeAppServiceData(type);
        } else {
            this.clearSettings();
            this.appService.loginMessage = res.message;
            this.appService.loginShowAlert = true;
            if (loader) loader.hide();
        }
        return res.status;
    }

    private async getAppVersion(): Promise<string> {
        try {
            let response = await this.http.get("version.cshtml", {
                observe: 'body',
                responseType: "text"
            }).toPromise();
            return response.trim();
        } catch (err) {
            return null;
        }
    }

    private async checkForNewerVersion() {
        if (!this.visualStateService.isMobile) {
            // Check for newer version and set reload flag
            let ver = await this.getAppVersion();
            if (ver && (ver != (serverConfig["pstVersion"]))) {
                this.shouldReload = true;
            }
        }
    }

    public async authenticate(type: string): Promise<boolean | undefined> {
        const token = this.getToken();
        let tokenValid = this.isTokenValid(token);
        if (type != 'ENPS') {
            if (!token || token == null || token === undefined || token === 'null' || !tokenValid) {
                let res = await this.ServiceFactory(type).authenticate();
                if (!res.status) {
                    this.clearSettings();
                } else {
                    if (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].loginSuccess) {
                        window["ANDROID_WRAPPER"].loginSuccess();
                    }
                    this.logonService.username = "";
                    this.logonService.password = "";
                    localStorage.removeItem("autoLoginWrapper");
                    localStorage.removeItem("username");
                }
            }
        } else {

            let res = await this.ServiceFactory(type).authenticate();
            if (res.status) {
                if (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].loginSuccess) {
                    window["ANDROID_WRAPPER"].loginSuccess();
                }
                this.saveWrapperCredentials();
                await this.checkForNewerVersion();
                this.initializeAppServiceData(type);
            } else {
                this.appService.loginMessage = res.message;
                this.appService.loginShowAlert = true;
            }
            return res.status;
        }
    }

    private saveWrapperCredentials() {
        if (window["webkit"] && window["webkit"].messageHandlers && window["webkit"].messageHandlers.interOp) {
            this.saveIOSWrapperCredentials();
        } else if (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].saveUserCredentials) {
            this.saveAndroidWrapperCredentials();
        }
    }
    private saveIOSWrapperCredentials() {
        if (this.visualStateService.hasIOSInterOp()) {
            if (this.visualStateService.autoLoginWrapper) {
                window["webkit"].messageHandlers.interOp.postMessage({ "name": "saveUserCredentials", "pw": this.logonService.password, "uname": this.logonService.username });
                localStorage.setItem("autoLoginWrapper", "true");
                this.logonService.rememberMe = true;
            } else if (!this.visualStateService.autoLoginWrapper && this.logonService.rememberMe) {
                window["webkit"].messageHandlers.interOp.postMessage({ "name": "saveUserCredentials", "pw": "", "uname": this.logonService.username });
                localStorage.removeItem("autoLoginWrapper");
            } else {
                window["webkit"].messageHandlers.interOp.postMessage({ "name": "saveUserCredentials", "pw": "", "uname": "" });
                localStorage.removeItem("autoLoginWrapper");
            }
        } else {
            localStorage.removeItem("autoLoginWrapper");
        }
    }

    private saveAndroidWrapperCredentials() {
        if (window["ANDROID_WRAPPER"] && window["ANDROID_WRAPPER"].saveUserCredentials) {
            if (this.visualStateService.autoLoginWrapper) {
                window["ANDROID_WRAPPER"].saveUserCredentials(this.logonService.username, this.logonService.password);
                localStorage.setItem("autoLoginWrapper", "true");
                this.logonService.rememberMe = true;
            } else if (!this.visualStateService.autoLoginWrapper && this.logonService.rememberMe) {
                window["ANDROID_WRAPPER"].saveUserCredentials(this.logonService.username, "");
                localStorage.removeItem("autoLoginWrapper");
            } else {
                window["ANDROID_WRAPPER"].saveUserCredentials("", "");
                localStorage.removeItem("autoLoginWrapper");
            }
        } else {
            localStorage.removeItem("autoLoginWrapper");
        }
    }

    private showMessage() {
        if (((this.visualStateService.isIOSDevice && this.visualStateService.isIpad) || (this.visualStateService.isAndroidDevice && this.visualStateService.isAndroidTablet)) && (window.orientation == 0 || window.orientation == 180) && !this.visualStateService.checkIfiOSWrapper()) {
            this.modalService.openAlert("Attention", "For the best Playbook experience please rotate your device to landscape.", "Ok");
        }
    }

    public async navigateToApplication() {
        let path: Array<string> = [this.appService.redirectedPath];

        this.showMessage();
        if (this.appService.redirectedPath) {
            if (this.appService.redirectedPath.indexOf('/search') > -1) {
                let chunks: Array<any> = this.appService.redirectedPath.split('/')
                if (chunks[4]) {
                    this.navigationService.enterSearchMode(chunks[chunks.length - 1])
                } else {
                    this.defaultNavigate();
                }
            } else {
                this.router.navigate(path);
            }
            this.appService.redirectedPath = null;
        } else {
            this.defaultNavigate();
        }
        return;
    }

    private defaultNavigate() {
        this.router.navigate(['/PST']);
    }

    private ServiceFactory(type: string): IAuthService {
        switch (type) {
            case 'ENPS':
                return this.enpsService;
            default:
                this.ssoService.providerName = type;
                return this.ssoService;
        }
    }

    private getSsoLogoutUrl(_providerName: string): string {
        if (!this.appService.ssoProviders)
            return "";
        let ssoLogoutUrl = "";
        let loginRedirectUrl = window.location.origin + window.location.pathname + "#/login";
        if ((_providerName == "MICROSOFT LIVE") || (_providerName == "MICROSOFTLIVE")) {
            let provider = this.appService.ssoProviders.filter(x => x.ID == "MICROSOFT LIVE");
            if (provider.length == 1) {
                return "https://login.microsoftonline.com/" + provider[0].ActiveDirectoryId + "/oauth2/logout?post_logout_redirect_uri=" + loginRedirectUrl;
            }
        }
        else if ((_providerName == "MICROSOFT AZURE") || (_providerName == "MICROSOFTAZURE")) {
            let provider = this.appService.ssoProviders.filter(x => x.ID == "MICROSOFT AZURE");
            if (provider.length == 1) {
                return "https://login.microsoftonline.com/" + provider[0].ActiveDirectoryId + "/oauth2/logout?post_logout_redirect_uri=" + loginRedirectUrl;
            }
        }
        else if ((_providerName == "OKTA")) {
            let provider = this.appService.ssoProviders.filter(x => x.ID == "OKTA");
            if (provider.length == 1) {
                return provider[0].BaseUrl + "/oauth2/v1/logout?id_token_hint=" + localStorage.getItem("idToken");
            }
        }
    }

    public async logout() {
        let _providerName = '';
        let _idToken = '';
        if (localStorage.getItem("providerName")) _providerName = localStorage.getItem("providerName");
        if (localStorage.getItem("idToken")) _idToken = localStorage.getItem("idToken");

        if (this.visualStateService.checkIfiOSWrapper() || this.visualStateService.checkIfAdroidWrapper()) {
            await this.logoutMobile();
        } else {

            let ssoLogoutUrl = this.getSsoLogoutUrl(_providerName);
            await this.navigationService.checkTeamSettingsState();
            let res = await this.logonService.logout();
            if (res.status) {
                // Reset navigation
                if (_providerName == "ONELOGIN") {
                    await this.logonService.logoutSSO(_providerName, _idToken);
                }

                await this.clearSettings();
                this.ServiceFactory(_providerName).logout();

                //this.timeoutQueue.setTimeout(() => {

                if (ssoLogoutUrl) window.location.href = ssoLogoutUrl;
                this.nomWebServicesJson.clearSessionData();
                this.router.navigate(['/login']);
                location.reload();
                //}, 10);
            }
        }
    }

    public async logoutMobile() {
        let _providerName = '';
        if (localStorage.getItem("providerName")) _providerName = localStorage.getItem("providerName");
        let res = await this.logonService.logout(localStorage.getItem("deviceTokenId"));
        if (res.status) {
            // Reset navigation
            this.clearSettings();
            this.ServiceFactory(_providerName).logout();
            window.location.assign(window.location.pathname);
        }
    }

    public async clearSettings() {
        this.appService.applicationName = null;
        try {
            await this.navigationService.setNavigationItem(null);
        } catch (e) { }
        this.navigationService.Navigation = null;
        this.navigationService.SystemCurrentNavigationItem = null;
        this.navigationService.SystemDefaultNavigationItem = null;
        this.navigationService.SystemNewNavigationItem = null;
        this.navigationService.SystemSearchNavigationItem = null;
        this.navigationService.DefaultNavigationItem = null;
        this.visualStateService.expanderSettingsMap = {};
        this.navigationService.inEditTeamSettingsMode = false;
        this.globalTablesService.clearDeskListItemCache();
        Util.clearLocalStorage();
    }

    private initializeAppServiceData(type: string) {
        this.globalTablesService.clearDeskListItemCache();
        for(let app in this.globalTablesService.clearServices) {
            this.globalTablesService.clearServices[app]();
        }
        this.visualStateService.getExpanderSettings();
        this.appService.loginMessage = "";
        this.appService.loginShowAlert = false;
        if (this.logonService.rememberMe) {
            localStorage.setItem("username", this.logonService.username);
        } else {
            localStorage.removeItem("username");
        }
        this.logonService.username = "";
        this.logonService.password = "";
        localStorage.setItem("providerName", type);

        this.navigateToApplication();
    }

    public getTokenExpirationDate(
        token: string
    ): Date | null {
        let decoded: any;
        decoded = jwtDecode<JwtPayload>(token);

        if (!decoded || !decoded.hasOwnProperty("exp")) {
            return null;
        }

        const date = new Date(0);
        date.setUTCSeconds(decoded.exp);

        return date;
    }

    public isTokenExpired(
        token: string
    ): boolean {
        if (!token || token === "") {
            return true;
        }
        const date = this.getTokenExpirationDate(token);

        if (date === null) {
            return false;
        }

        return !(date.valueOf() > new Date().valueOf());
    }
}
